import React from 'react';
import * as Icons from 'lucide-react';
import { Switch } from './Switch';
import { Badge } from './Badge';
import { Button } from './Button';
import { Pencil, Trash2, Download } from 'lucide-react';
import { PersonaWithSettings } from '../endpoints/personas/list_GET.schema';
import styles from './PersonaCard.module.css';
import { safeCap } from '../helpers/stringUtils';

interface PersonaCardProps {
  persona: PersonaWithSettings;
  onToggle: (personaId: string, enabled: boolean) => void;
  onEdit?: (persona: PersonaWithSettings) => void;
  onDelete?: (personaId: string) => void;
  onExport?: () => void;
  isToggling?: boolean;
}

export const PersonaCard: React.FC<PersonaCardProps> = ({
  persona,
  onToggle,
  onEdit,
  onDelete,
  onExport,
  isToggling,
}) => {
  // Get the icon component dynamically from lucide-react
  const IconComponent =
    (Icons as any)[
      persona.icon
        .split('-')
        .map((part, i) => (i === 0 ? part : safeCap(part)))
        .join('')
    ] || Icons.Sparkles;

  const categoryLabels: Record<string, string> = {
    development: 'Development',
    security: 'Security',
    operations: 'Operations',
    documentation: 'Documentation',
    compliance: 'Compliance',
    governance: 'Governance',
    custom: 'Custom',
  };

  const categoryColors: Record<
    string,
    'default' | 'secondary' | 'success' | 'warning' | 'destructive'
  > = {
    development: 'default',
    security: 'destructive',
    operations: 'warning',
    documentation: 'secondary',
    compliance: 'destructive',
    governance: 'warning',
    custom: 'success',
  };

  return (
    <div className={`${styles.card} ${persona.isEnabled ? styles.enabled : styles.disabled}`}>
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <IconComponent size={24} />
        </div>
        <div className={styles.info}>
          <h4 className={styles.name}>{persona.name}</h4>
          <Badge variant={categoryColors[persona.category] || 'success'} className={styles.badge}>
            {categoryLabels[persona.category] || persona.category.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
          </Badge>
        </div>
        <div className={styles.toggle}>
          <Switch
            checked={persona.isEnabled}
            onCheckedChange={(checked) => onToggle(persona.id, checked)}
            disabled={isToggling}
          />
        </div>
      </div>

      {persona.description && <p className={styles.description}>{persona.description}</p>}

      {persona.tags && persona.tags.length > 0 && (
        <div className={styles.tags}>
          {persona.tags.map((tag, index) => (
            <span key={index} className={styles.tag}>
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <span className={styles.priority}>
          Priority: {persona.customPriority ?? persona.priority}
        </span>
        <div className={styles.actions}>
          {onExport && (
            <Button variant="ghost" size="icon-sm" onClick={onExport} aria-label="Export persona">
              <Download size={14} />
            </Button>
          )}
          {!persona.isBuiltin && onEdit && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onEdit(persona)}
              aria-label="Edit persona"
            >
              <Pencil size={14} />
            </Button>
          )}
          {!persona.isBuiltin && onDelete && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                if (confirm('Are you sure you want to delete this persona?')) {
                  onDelete(persona.id);
                }
              }}
              aria-label="Delete persona"
            >
              <Trash2 size={14} />
            </Button>
          )}
          {persona.isBuiltin && <span className={styles.builtinBadge}>Built-in</span>}
        </div>
      </div>
    </div>
  );
};
